import { useEffect } from 'react';
import { Col, Row, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';

import { RootState } from '../../../../state/store';
import { Breadcrumb } from '../../../AppHeader/interfaces';
import { getCurrentTheme } from '../../../../helpers/theme';
import { transferStatuses } from '../../../../config/commons';
import * as weighingTransfersSlice from '../weighingTransfersSlice';
import IconButton from '../../../../common/components/buttons/IconButton';
import { isTransferTypeCapsule } from '../../../../helpers/transfer-stocking';
import Breadcrumbs from '../../../../common/components/Breadcrumb/Breadcrumbs';
import { getTitleByStockingPhaseType } from '../../../../helpers/stocking.helpers';
import { changeBreadcrumb, changeHeader, fetchCompany, setPhaseType } from '../../../AppHeader/headerSlice';

import { Transport } from './Transport';
import styles from './Transfers.module.scss';
import { MiniCharts } from './Charts/MiniCharts';
import { TransferSummary } from './TransferSummary';
import { TransferHistogram } from './Charts/TransferHistogram';
import { TransferMetricChart } from './Charts/TransferMetricChart';
import { StockingDestinationInformation } from './StockingDestinationInformation';

type TParams = { stockingId: string; transferId: string; };

export const Transfers = ({ match }: RouteComponentProps<TParams>) => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const theme = getCurrentTheme();

  const stockingId = match.params.stockingId;
  const transferId = match.params.transferId;

  const { selectedStocking, capsules, isDownloadingTransferPdf } = useSelector((state: RootState) => state.weighingTransfers);
  const { breadcrumb, company } = useSelector((state: RootState) => state.header);

  const { _id: companyId } = company;
  const { transfer } = capsules;

  /*useEffect section*/
  useEffect(() => {
    dispatch(changeHeader({ title: 'stockings.weighingTransfers' }));
  }, [dispatch]);

  useEffect(() => {
    if (!selectedStocking?.name || !transfer?.name) {
      return;
    }

    const breadcrumb: Breadcrumb[] = [{
      id: 'sowings',
      name: getTitleByStockingPhaseType(selectedStocking.phaseType),
      url: '/production/stockings',
    },
    {
      id: selectedStocking.name,
      name: `${t('stockings.stocking')} ${selectedStocking.name}`,
      url: `/production/stockings/${stockingId}`,
    },
    {
      id: 'weighing-transfers',
      name: t('stockings.weighingTransfers'),
      url: `/production/stockings/${stockingId}/weighing-transfers`,
    },
    {
      id: 'transfer',
      name: transfer.name,
      url: `/production/stockings/${stockingId}/weighing-transfers/${transferId}`,
    }];

    dispatch(changeBreadcrumb(breadcrumb));
    dispatch(setPhaseType(selectedStocking.phaseType));
  }, [dispatch, stockingId, transferId, selectedStocking?.name, selectedStocking?.phaseType, transfer?.name]);

  useEffect(() => {
    if (selectedStocking?._id) {
      return;
    }
    
    dispatch(weighingTransfersSlice.fetchStocking({ stockingId }));
  }, [dispatch, stockingId]);

  useEffect(() => {
    if (companyId || !selectedStocking?._id) {
      return;
    }

    dispatch(fetchCompany(selectedStocking.companyId));
  }, [dispatch, companyId, selectedStocking?.companyId]);

  useEffect(() => {
    if (!companyId) {
      return;
    }

    dispatch(weighingTransfersSlice.fetchTrays({ companyId, transferId }));
    dispatch(weighingTransfersSlice.fetchParameters({ companyId, transferId }));
    dispatch(weighingTransfersSlice.fetchTransports(companyId));
    dispatch(weighingTransfersSlice.fetchTransferChartData({ transferId }));
  }, [dispatch, stockingId, transferId, companyId]);
  /*End of useEffect section*/

  /*render components section */
  const renderButtonGeneratePdf = () => {
    if (!transfer?.name || transfer.status !== transferStatuses.COMPLETED) {
      return null;
    }

    if (!isTransferTypeCapsule(transfer?.transports)) {
      return null;
    }

    return (
      <IconButton
        className={styles.generatePdf}
        disabled={!transfer?.name}
        onClick={() => {
          dispatch(weighingTransfersSlice.downloadTransferPdf({ transferId, transferName: transfer.name, companyId }));
        }}
        loading={isDownloadingTransferPdf}
        iconName='download'
        placement='left'
        tooltipText={t('survivalRate.generate')}
      />
    );
  };

  const renderSubHeader = () => {
    if (!selectedStocking?._id) {
      return null;
    }

    return (
      <Row className={styles.rowHeader} align='middle' justify='space-between'>
        <Breadcrumbs breadcrumb={breadcrumb} noCapitalize theme={theme} />
        {renderButtonGeneratePdf()}
      </Row>
    );
  };

  const renderTransports = () => {
    if (!transfer?._id) {
      return null;
    }
    
    return transfer.transports.map((transport) => {
      return (
        <>
          <Transport
            transport={transport}
            maxSmallAnimalsWeight={transfer.maxSmallAnimalsWeight}
            theme={theme}
            maxSmallAnimalsPercentage={transfer.maxSmallAnimalsPercentage}
          />
        </>
      );
    });
  };
  /* End of render components section */

  return (
    <div className={styles.weighingTransfers}>
      <Row className={styles.rowFlex}>
        <Col className={styles.columnFlex} >
          {renderSubHeader()}
          
          {!!transfer?._id && <Space direction='vertical' size={24}>
            <TransferSummary theme={theme} />
            {isTransferTypeCapsule(transfer?.transports) && <MiniCharts theme={theme}/>}
          </Space>}
          
          <div className={styles.transports}>
            {renderTransports()}
          </div>
        </Col>
      </Row>

      <StockingDestinationInformation theme='light'/>
      <TransferMetricChart theme='light' />
      <TransferHistogram theme='light' />
    </div>
  );
};
